import { lazy, Suspense, useEffect, useState } from 'react';
import { api } from './api.js';
import { matchPath, navigate, useRoute } from './router.js';
import { LandingPage } from './pages/LandingPage.js';
import { BootSplash } from './ui/BootSplash.js';

/*
 * The app (every signed-in screen, the studio, admin, billing) is a separate
 * download. The landing page used to ship inside one 2MB script with all of
 * it, and nothing on the page could paint until that script had arrived and
 * run, which on a phone was six seconds of a blank screen.
 */
const App = lazy(() => import('./App.js').then((module) => ({ default: module.App })));
// The blog's pages carry every article's text; they arrive when one is opened.
const BlogPage = lazy(() =>
  import('./pages/BlogPage.js').then((module) => ({ default: module.BlogPage })),
);
const BlogPostPage = lazy(() =>
  import('./pages/BlogPostPage.js').then((module) => ({ default: module.BlogPostPage })),
);
const LegalPage = lazy(() =>
  import('./pages/LegalPage.js').then((module) => ({ default: module.LegalPage })),
);
const RoadmapPage = lazy(() =>
  import('./pages/RoadmapPage.js').then((module) => ({ default: module.RoadmapPage })),
);
// Who is behind Krevya: read by a visitor deciding whether to trust the
// product, and by anyone verifying the company from outside.
const AboutPage = lazy(() =>
  import('./pages/AboutPage.js').then((module) => ({ default: module.AboutPage })),
);

/**
 * The pages anyone can read without an account, which the app has always
 * drawn without waiting for the session: they are the same for everybody.
 */
function isMarketingPath(path: string): boolean {
  return (
    path === '/' ||
    path === '/blog' ||
    path === '/roadmap' ||
    path === '/about' ||
    matchPath('/blog/:slug', path) !== null ||
    matchPath('/legal/:doc', path) !== null
  );
}

/**
 * The marketing domain holds no session: sign-in lives on the app host and the
 * apex sends every API call there, where the browser refuses it as a
 * cross-origin redirect. Asking there only adds a failed request and a console
 * error to every visit.
 */
const APEX_HOSTS = new Set(['krevya.com', 'www.krevya.com']);

/**
 * What decides between the public pages and the app.
 *
 * A visitor who only ever reads public pages never downloads the app. Once any
 * app screen has been needed the app takes over for good, including the public
 * pages it already knows how to draw, so walking from a screen to the blog and
 * back keeps the session and the rail exactly as they were.
 *
 * `prerendered` is true when the page arrived as the HTML the build rendered,
 * which React is adopting rather than drawing.
 */
export function Root({ prerendered }: { prerendered: boolean }) {
  const route = useRoute();
  const marketing = isMarketingPath(route.path);
  const [appNeeded, setAppNeeded] = useState(!marketing);
  // Set during render, not in an effect, so the public page is never drawn
  // for a frame on the way into the app.
  if (!marketing && !appNeeded) setAppNeeded(true);

  if (appNeeded || !marketing) {
    return (
      <Suspense fallback={<BootSplash />}>
        <App />
      </Suspense>
    );
  }

  if (route.path === '/') return <Landing prerendered={prerendered} />;
  const blogPost = matchPath('/blog/:slug', route.path);
  const legal = matchPath('/legal/:doc', route.path);
  return (
    <Suspense fallback={<BootSplash />}>
      {route.path === '/blog' ? <BlogPage /> : null}
      {blogPost?.['slug'] ? <BlogPostPage slug={blogPost['slug']} /> : null}
      {legal?.['doc'] ? <LegalPage doc={legal['doc']} /> : null}
      {route.path === '/roadmap' ? <RoadmapPage /> : null}
      {route.path === '/about' ? <AboutPage /> : null}
    </Suspense>
  );
}

/**
 * The root is the landing page for a visitor and the app for a customer.
 *
 * Where a session can exist, the same probe the app runs decides which, and a
 * signed-in merchant is sent on to `/app`. While it runs, the mark shows, as it
 * always has, unless the page arrived prerendered: then the landing page is
 * already on screen and stays there, because swapping finished content for a
 * splash would be a step backwards.
 */
function Landing({ prerendered }: { prerendered: boolean }) {
  const [probe] = useState(
    () => typeof window !== 'undefined' && !APEX_HOSTS.has(window.location.hostname),
  );
  const [checked, setChecked] = useState(!probe);

  useEffect(() => {
    if (!probe) return;
    let cancelled = false;
    api
      .me()
      .then(({ workspaces }) => {
        if (cancelled) return;
        if (workspaces[0]) navigate('/app');
        else setChecked(true);
      })
      .catch(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [probe]);

  if (!checked && !prerendered) return <BootSplash />;
  return <LandingPage />;
}
